import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { IconButton } from '@flipster/react/design-system/icon-button';
import CopyIcon from '@flipster/react/icon/generated/CopyIcon';
import ShareIcon from '@flipster/react/icon/generated/ShareIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import webviewHandler from '@flipster/utils/webview-handler';

import { referralCodeBunja } from '../shared/referral-code/state';
import { getFlipsterUrl } from '../shared/urls';
import { useIsWebview } from '../shared/webview';
import { useCopy } from '../shared/webview/clipboard';

const getShareUrl = (referralCode: string, refferalType: 'copy' | 'share') =>
  `${getFlipsterUrl()}/signup?referral_code=${referralCode ?? ''}&referral_type=${refferalType}`;

export const ReferralCodeDesktop = () => {
  const copy = useCopy();
  const { referralCodeAtom } = useBunja(referralCodeBunja);
  const referralCode = useAtomValue(referralCodeAtom);

  if (!referralCode) return null;

  const onCopyClick = (text: string) => copy(text);
  const shareUrl = getShareUrl(referralCode, 'copy');

  return (
    <div
      className={hstack({
        gap: 32,
        color: 'content.default.level-5',
        bgColor: 'background.default.level-1',
        rounded: 8,
        p: 24,
        '& > div': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          alignSelf: 'stretch',
        },
      })}
    >
      <div>
        <p className={css({ textStyle: 'Text/L/Strong' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Referral_code' />
        </p>
        <div className={copyItemStyle}>
          <p className={css({ flex: 1, textStyle: 'Text/L/Regular' })}>{referralCode}</p>
          <IconButton
            icon={<CopyIcon />}
            hierarchy='quaternary'
            size='small'
            onClick={() => onCopyClick(referralCode)}
          />
        </div>
      </div>
      <div>
        <p className={css({ textStyle: 'Text/L/Strong' })}>
          <FormattedMessage id='Autogen_common.r251126.s_link' />
        </p>
        <div className={copyItemStyle}>
          <p className={css({ flex: 1, textStyle: 'Text/L/Regular' })}>{shareUrl}</p>
          <IconButton icon={<CopyIcon />} hierarchy='quaternary' size='small' onClick={() => onCopyClick(shareUrl)} />
        </div>
      </div>
    </div>
  );
};

export const ReferralCodeMobileFooter = () => {
  const copy = useCopy();
  const isWebview = useIsWebview();
  const { referralCodeAtom } = useBunja(referralCodeBunja);
  const referralCode = useAtomValue(referralCodeAtom);

  if (!referralCode) return null;

  const shareUrl = getShareUrl(referralCode, 'share');
  const shareMessage = `You're invited to Flipster! ${shareUrl}`;
  const onCopyClick = () => copy(referralCode);
  const onShareClick = () => {
    webviewHandler.postMessage(
      {
        action: 'share',
        params: {
          message: `You're invited to Flipster! ${shareUrl}`,
        },
      },
      () => {
        if (navigator.share) {
          navigator.share({ title: shareMessage, url: shareUrl });
          return;
        }
        copy(shareUrl);
      },
    );
  };

  return (
    <footer
      className={[
        stack({
          position: isWebview ? 'fixed' : 'relative',
          insetX: 0,
          bottom: 0,
          pb: 20,
          px: isWebview ? 16 : 0,
          gap: 12,
          w: '100%',
          bgColor: 'background.default.level-0',
        }),
        css({
          position: 'relative',
          _before: {
            content: '""',
            position: 'absolute',
            top: -20,
            left: 0,
            right: 0,
            height: 20,
            background: 'linear-gradient(to bottom, #11111100, #111111)',
            pointerEvents: 'none',
            zIndex: 1,
          },
        }),
      ].join(' ')}
    >
      <div className={copyItemStyle}>
        <p className={css({ textStyle: 'Text/L/Strong' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Referral_code' />
        </p>
        <p className={css({ flex: 1, textStyle: 'Text/L/Regular', textAlign: 'right' })}>{referralCode}</p>
        <IconButton icon={<CopyIcon />} hierarchy='quaternary' size='small' onClick={onCopyClick} />
      </div>
      <Button
        color='accent'
        hierarchy='primary'
        label={<FormattedMessage id='Autogen_common.r251126.s_Share_link' />}
        icon={<ShareIcon />}
        onClick={onShareClick}
      />
    </footer>
  );
};

const copyItemStyle = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'left',
  gap: 12,
  color: 'content.default.level-5',
  bgColor: 'background.default.level-3',
  rounded: 4,
  py: 12,
  px: 16,
});
